/**
 * App shell = the Stage console chrome promoted to the product (copied from site Stage.tsx: chrome, nav rail, main).
 * Dropped from the demo: replay clock, director/captions, try-mode, speed/scrubber. Added: router links, tenant/user
 * menu, stream status pill, alerts bell, ⌘K palette, fixture-mode badge. Full viewport (`is-fs stg--app`).
 */
import { Suspense, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Icon } from '@ui/Icon';
import { useData } from '@app/core/provider';
import { useSession } from '@app/core/auth';
import { startRealtime } from '@app/core/realtime';
import { useStreamStatus } from '@app/core/stream';
import { tzAbbr } from '@app/core/format';
import { setTelemetryFixtures } from '@app/core/telemetry';
import { AlertsBell } from '@app/features/today/Alerts';
import { visibleNav } from './nav';
import { Palette } from './Palette';
import { UserMenu } from './UserMenu';

function useLayout() {
  const f = (w: number) => (w >= 1060 ? 'desktop' : w >= 700 ? 'tablet' : 'phone');
  const [l, setL] = useState(() => f(window.innerWidth));
  useEffect(() => { const h = () => setL(f(window.innerWidth)); addEventListener('resize', h); return () => removeEventListener('resize', h); }, []);
  return l;
}

export function Shell() {
  const { data } = useData();
  const { me, can, entitled, flag } = useSession();
  const qc = useQueryClient();
  const layout = useLayout();
  const loc = useLocation();
  const nav = useNavigate();
  const [palette, setPalette] = useState(false);
  const { status, degraded } = useStreamStatus();

  useEffect(() => { setTelemetryFixtures(data.mode === 'fixture'); return startRealtime(data, qc); }, [data, qc]);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPalette(true); }
    };
    addEventListener('keydown', h);
    return () => removeEventListener('keydown', h);
  }, []);

  const items = useMemo(() => visibleNav(can, flag), [can, flag]);
  const view = loc.pathname.split('/')[1] || 'today';
  const initials = me.tenant.name.split(/\s+/).filter((w) => !/^(llc|inc|co|the|&|and)$/i.test(w)).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  return (
    <div className={['stg', 'theme-dark', 'is-fs', 'stg--app', `stg--${layout}`].join(' ')} data-view={view}>
      <a className="app-skip" href="#main">Skip to content</a>
      <div className="stg-chrome">
        <span className="stg-shopmark" aria-hidden>{initials}</span>
        <span className="stg-chrome__title"><b>{me.tenant.name}</b><span> · {tzAbbr(me.tenant.timezone)}</span></span>
        {data.mode === 'fixture' && <span className="stg-watermark" title="In-memory Lone Star sample tenant (dev/demo build)">Sample data</span>}
        <span className={`app-live app-live--${status}`} role="status" title={degraded ? 'Live updates paused; refreshing every 30 s' : status === 'live' ? 'Live' : 'Reconnecting…'}>
          <i aria-hidden />{status === 'live' ? 'Live' : degraded ? 'Live updates paused' : 'Reconnecting…'}
        </span>
        <button type="button" className="app-search" onClick={() => setPalette(true)} aria-keyshortcuts="Meta+K Control+K">
          <Icon name="search" size={14} /><span>Search</span><kbd>⌘K</kbd>
        </button>
        <AlertsBell />
        <UserMenu />
      </div>
      <div className="stg-body">
        <nav className="stg-nav" aria-label="Sections">
          <img className="stg-nav__brand" src="/brand/app-mark.svg" width={28} height={28} alt="" />
          {items.map((n) => {
            const locked = n.module && !entitled(n.module);
            return (
              <NavLink key={n.path} to={n.path} className={({ isActive }) => ['stg-nav__item', isActive && 'is-active', locked && 'is-locked'].filter(Boolean).join(' ')}>
                <Icon name={n.icon} size={18} />
                <span>{n.label}</span>
                {locked && <i className="app-nav__lock" aria-label="Not in your plan"><Icon name="lock" size={10} /></i>}
              </NavLink>
            );
          })}
        </nav>
        <main className="stg-main" id="main" tabIndex={-1}>
          <Suspense fallback={<div className="app-loading" aria-busy="true"><span className="stg-dots"><i /><i /><i /></span></div>}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <Palette open={palette} onClose={() => setPalette(false)} onGo={(to) => { setPalette(false); nav(to); }} />
    </div>
  );
}
